/**
 * Scrolls a table cell into view, accounting for sticky headers/footers and pinned columns.
 * Used for keyboard navigation to ensure focused cells are visible.
 *
 * @param element - The cell element to scroll into view
 * @param stickyDimensions - The dimensions of pinned columns
 * @param stickyDimensions.leftWidth - Width of left-pinned columns
 * @param stickyDimensions.rightWidth - Width of right-pinned columns
 */
export function scrollCellIntoView(
  element: HTMLElement,
  stickyDimensions: { leftWidth: number; rightWidth: number },
): void {
  const isBodyCell = element.getAttribute("data-anv") === "table-body-cell";

  const scrollContainer = element.closest(
    '[data-anv="table-scroll-container"]',
  ) as HTMLElement | null;

  if (!scrollContainer) return;

  const headerElement =
    scrollContainer.querySelectorAll('[role="rowgroup"]')[0];
  const footerElement = scrollContainer
    .querySelector('[data-anv="table-footer-row"]')
    ?.closest('[role="rowgroup"]') as HTMLElement | undefined;

  const dimensions = {
    headerHeight: headerElement?.getBoundingClientRect().height ?? 0,
    footerHeight: footerElement?.getBoundingClientRect().height ?? 0,
    leftWidth: stickyDimensions.leftWidth,
    rightWidth: stickyDimensions.rightWidth,
  };

  const elementRect = element.getBoundingClientRect();
  const scrollContainerRect = scrollContainer.getBoundingClientRect();

  // Use clientWidth/clientHeight instead of bounding rect dimensions.
  // getBoundingClientRect includes scrollbars, but sticky elements position
  // relative to the content area which excludes scrollbars.
  const contentWidth = scrollContainer.clientWidth;
  const contentHeight = scrollContainer.clientHeight;

  // Calculate visible area (excluding sticky regions)
  const visibleLeft = scrollContainerRect.x + dimensions.leftWidth;
  const visibleRight =
    scrollContainerRect.x + contentWidth - dimensions.rightWidth;
  const visibleTop = scrollContainerRect.y + dimensions.headerHeight;
  const visibleBottom =
    scrollContainerRect.y + contentHeight - dimensions.footerHeight;

  // Check if element is fully visible within the non-sticky area
  const isFullyVisible =
    elementRect.x >= visibleLeft &&
    elementRect.x + elementRect.width <= visibleRight &&
    (isBodyCell
      ? elementRect.y >= visibleTop &&
        elementRect.y + elementRect.height <= visibleBottom
      : true);

  if (isFullyVisible) return;

  // Calculate scroll offsets
  const getScrollByLeft = (): number => {
    // Element is under left pinned columns - scroll left to bring left edge to visibleLeft
    if (elementRect.x < visibleLeft) {
      return elementRect.x - visibleLeft;
    }
    // Element is under right pinned columns - scroll right to bring right edge to visibleRight
    if (elementRect.x + elementRect.width > visibleRight) {
      return elementRect.x + elementRect.width - visibleRight;
    }
    return 0;
  };

  const getScrollByTop = (): number => {
    if (!isBodyCell) return 0;
    // Element is under header - scroll up to bring top of element to visibleTop
    if (elementRect.y < visibleTop) {
      return elementRect.y - visibleTop;
    }
    // Element is under footer - scroll down to bring bottom of element to visibleBottom
    if (elementRect.y + elementRect.height > visibleBottom) {
      return elementRect.y + elementRect.height - visibleBottom;
    }
    return 0;
  };

  const scrollLeft = getScrollByLeft();
  const scrollTop = getScrollByTop();

  if (scrollLeft !== 0 || scrollTop !== 0) {
    scrollContainer.scrollBy({
      left: scrollLeft,
      top: scrollTop,
      behavior: "smooth",
    });
  }
}
